import { type FC } from "react";
import { useController, useFormContext } from "react-hook-form";
import { Stack } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import { ContactPersonInput, AddOrUpdateContactPersonDialog } from "../../shared/ContactPerson";
import type { MultipleContactPersonValue } from "../types";
import type { InputProps } from "../DynamicInput";
import { useDynamicFormState } from "../DynamicFormProvider";
import { ErrorMessage } from "../inlineForm/ErrorMessage";
import { AddDialogTrigger } from "./AddDialogTrigger";
import { isAnswerRequestEmpty } from "./isAnswerRequestEmpty";

type MultipleContactPersonBaseInputProps = InputProps<MultipleContactPersonValue> & {
  isMultistepFormVersion: boolean;
};

export const MultipleContactPersonInputBase: FC<MultipleContactPersonBaseInputProps> = ({
  readonly,
  isMultistepFormVersion,
  questionCard,
  onSave,
}) => {
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();
  const { t } = useTranslation();
  const { control } = useFormContext();
  const saveMutation = useMutation({ mutationFn: (value: MultipleContactPersonValue) => onSave(value) });

  const { field } = useController({ control, name: questionCard.id });

  const fieldValue = (field.value ?? []) as MultipleContactPersonValue;

  const updateContactPerson = (index: number, value: MultipleContactPersonValue[number]) => {
    const newValue = fieldValue.map((contactPerson, i) => (i === index ? value : contactPerson));
    return saveMutation.mutateAsync(newValue, { onSuccess: () => field.onChange(newValue) });
  };

  const addContactPerson = (value: MultipleContactPersonValue[number]) => {
    const newValue = [...fieldValue, value];
    return saveMutation.mutateAsync(newValue, { onSuccess: () => field.onChange(newValue) });
  };

  const removeContactPerson = (index: number) => {
    const newValue = fieldValue.filter((_, i) => i !== index);
    field.onChange(newValue);
    return saveMutation.mutateAsync(newValue);
  };

  return (
    <Stack sx={{ mb: 0.5 }}>
      {fieldValue.map((contactPerson, index) => (
        <ContactPersonInput
          key={index}
          contactPerson={contactPerson}
          disabled={readonly}
          onEdit={updatedContactPerson => updateContactPerson(index, updatedContactPerson)}
          onDelete={() => removeContactPerson(index)}
        />
      ))}
      <AddDialogTrigger
        isMultistepFormVersion={isMultistepFormVersion}
        isEmpty={fieldValue.length === 0}
        isReadOnly={readonly}
        renderDialog={props => <AddOrUpdateContactPersonDialog {...props} onSubmit={addContactPerson} />}
      >
        {`${t("DynamicForm.MultiContactPersonInput.AddContactPerson")}`}
      </AddDialogTrigger>
      {saveMutation.isError && <ErrorMessage />}
      {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
        <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
      )}
    </Stack>
  );
};
